import { useEffect, useState } from "react";
import { api, formatErr, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { handleSimpleFormNav } from "@/lib/keyboardNav";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Boxes, Search, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import ImportExportBar from "@/components/ImportExportBar";
import { useAuth } from "@/context/AuthContext";
import { PaginationBar, usePagination } from "@/components/Pagination";

const empty = {
  part_number: "", item_name: "", unit: "pcs", company: "", group: "", category: "",
  hsn_code: "", sector: "", item_code: "", warranty: "No", warranty_months: "", cgst: 9, sgst: 9, mrp: 0, per_unit_cost: 0, conversion: 1, min_order_qty: 1,
  reorder_level: 0, lead_time_days: 7, preferred_supplier_id: "", reorder_formula: "reorder_level",
};

const baseFields = [
  ["part_number", "Part Number"], ["item_name", "Item Name"], ["unit", "Unit"],
];

const numFields = [
  ["cgst", "CGST %"], ["sgst", "SGST %"],
  ["per_unit_cost", "Per Unit Cost (₹)"],
  ["mrp", "MRP (₹)"], ["conversion", "Conversion"], ["min_order_qty", "Min. Order Qty"],
  ["reorder_level", "Reorder Level"], ["lead_time_days", "Lead Time (days)"],
];

const ADD_CFG = {
  company:  { endpoint: "/companies",  label: "Company",  field: "company" },
  category: { endpoint: "/categories", label: "Category", field: "category" },
  group:    { endpoint: "/groups",     label: "Group",    field: "group" },
  hsn:      { endpoint: "/hsn-codes",  label: "HSN Code", field: "hsn_code" },
  sector:   { endpoint: "/sectors",    label: "Sector",   field: "sector" },
};

const MasterSelect = ({ label, testid, value, onChange, options, optKey, disabled, onAdd }) => (
  <div>
    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{label} *</Label>
    <Select value={value || ""} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger data-testid={testid} className="mt-1 rounded-sm bg-secondary border-border">
        <SelectValue placeholder={disabled ? `Select ${label === "Company" ? "company first" : "parent first"}` : `Select ${label}`} />
      </SelectTrigger>
      <SelectContent className="bg-card border-border">
        {options.map(o => (
          <SelectItem key={o.id} data-testid={`${testid}-opt-${o[optKey]}`} value={o[optKey]}>{(o.group_code || o.category_code || o.company_code) ? `${o.group_code || o.category_code || o.company_code} · ${o[optKey]}` : o[optKey]}</SelectItem>
        ))}
      </SelectContent>
    </Select>
    {!disabled && options.length === 0 && (
      <button type="button" data-testid={`${testid}-add-new`} onClick={onAdd} className="text-xs text-primary mt-1 hover:underline">
        No {label.toLowerCase()} found — + Add New
      </button>
    )}
  </div>
);

export default function ItemMaster() {
  const { user } = useAuth();
  const canOverrideCode = ["owner", "manager"].includes(user.role);
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [fCompany, setFCompany] = useState("all");
  const [fCategory, setFCategory] = useState("all");
  const [fGroup, setFGroup] = useState("all");
  const [fHsn, setFHsn] = useState("all");
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [groups, setGroups] = useState([]);
  const [hsnCodes, setHsnCodes] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [addDlg, setAddDlg] = useState(null);
  const [af, setAf] = useState({ name: "", description: "", cgst: 9, sgst: 9 });
  const lockNonPrice = !user?.is_platform_admin && !!editId;
  const load = () => api.get("/items").then(r => setList(r.data));
  const loadCompanies = () => api.get("/companies").then(r => setCompanies(r.data));
  const loadCategories = () => api.get("/categories").then(r => setCategories(r.data));
  const loadGroups = () => api.get("/groups").then(r => setGroups(r.data));
  const loadHsn = () => api.get("/hsn-codes").then(r => setHsnCodes(r.data));
  const loadSectors = () => api.get("/sectors").then(r => setSectors(r.data));
  useEffect(() => { load(); loadCompanies(); loadCategories(); loadGroups(); loadHsn(); loadSectors(); }, []);

  const catOpts = categories;
  const grpOpts = groups;
  const hsnOpts = hsnCodes;

  const codeOf = (arr, name, key) => (arr.find(o => o.name === name) || {})[key] || "";
  const secCode = codeOf(sectors, f.sector, "sector_code");
  const grpCode = codeOf(groups, f.group, "group_code");
  const catCode = codeOf(categories, f.category, "category_code");
  const cmpCode = codeOf(companies, f.company, "company_code");
  const codePreview = (secCode && grpCode && catCode && cmpCode) ? `${secCode}-${grpCode}-${catCode}-${cmpCode}` : "";

  const saveNew = async () => {
    const cfg = ADD_CFG[addDlg];
    try {
      const payload = addDlg === "hsn"
        ? { hsn_code: af.name, category: f.category || null, description: af.description || null, cgst: Number(af.cgst), sgst: Number(af.sgst) }
        : { name: af.name, description: af.description || null };
      const res = await api.post(cfg.endpoint, payload);
      toast.success(`${cfg.label} added`);
      if (addDlg === "company") { await loadCompanies(); setF(x => ({ ...x, company: res.data.name })); }
      if (addDlg === "category") { await loadCategories(); setF(x => ({ ...x, category: res.data.name })); }
      if (addDlg === "group") { await loadGroups(); setF(x => ({ ...x, group: res.data.name })); }
      if (addDlg === "hsn") { await loadHsn(); setF(x => ({ ...x, hsn_code: res.data.hsn_code })); }
      if (addDlg === "sector") { await loadSectors(); setF(x => ({ ...x, sector: res.data.name })); }
      setAddDlg(null); setAf({ name: "", description: "", cgst: 9, sgst: 9 });
    } catch (e) { toast.error(formatErr(e)); }
  };

  const save = async () => {
    try {
      const payload = { ...f,
        cgst: Number(f.cgst), sgst: Number(f.sgst), mrp: Number(f.mrp),
        per_unit_cost: Number(f.per_unit_cost) || 0,
        conversion: Number(f.conversion), min_order_qty: Number(f.min_order_qty),
        reorder_level: Number(f.reorder_level || 0), lead_time_days: Number(f.lead_time_days || 7),
        preferred_supplier_id: f.preferred_supplier_id || null,
        reorder_formula: f.reorder_formula || "reorder_level",
        warranty_months: f.warranty_months !== "" && f.warranty_months != null ? Number(f.warranty_months) : null,
        item_code: f.item_code || null,
      };
      if (editId) await api.patch(`/items/${editId}`, payload);
      else await api.post("/items", payload);
      toast.success(editId ? "Item updated" : "Item added");
      setF(empty); setEditId(null); setOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const edit = (it) => {
    setF({ ...empty, ...it });
    setEditId(it.id); setOpen(true);
  };

  const del = async (id) => {
    if (!confirm("Delete this item?")) return;
    try { await api.delete(`/items/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const openNew = () => { setF(empty); setEditId(null); setOpen(true); };

  // Full-screen form: hide sidebar while the item form is open (same pattern as transaction forms)
  useEffect(() => {
    document.body.classList.toggle("txn-form-fullscreen", open);
    return () => document.body.classList.remove("txn-form-fullscreen");
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => document.querySelector('[data-testid="item-part_number"]')?.focus(), 120);
    return () => clearTimeout(t);
  }, [open]);

  const filtered = list.filter(it =>
    (!q || it.item_name.toLowerCase().includes(q.toLowerCase()) ||
    it.part_number.toLowerCase().includes(q.toLowerCase()) ||
    (it.item_code || "").toLowerCase().includes(q.toLowerCase()) ||
    (it.company || "").toLowerCase().includes(q.toLowerCase())) &&
    (fCompany === "all" || it.company === fCompany) &&
    (fCategory === "all" || it.category === fCategory) &&
    (fGroup === "all" || it.group === fGroup) &&
    (fHsn === "all" || it.hsn_code === fHsn)
  );
  const pg = usePagination(filtered, q);

  return (
    <div className="p-8">
      {!open && (<>
      <div className="flex items-end justify-between mb-6 gap-4">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><Boxes className="w-3 h-3"/> Owner / Manager only · Auto-fills unit price on job cards</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Item Master</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <Input data-testid="item-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="pl-8 rounded-sm bg-secondary border-border w-56" />
          </div>
          <ImportExportBar masterKey="items" filters={{ search: q }} onImported={load}/>
          <Button data-testid="add-item-btn" onClick={openNew} className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] font-bold text-xs">
            <Plus className="w-4 h-4 mr-1"/> New Item
          </Button>
        </div>
      </div>
      </>)}

      {open && (
      <div className="border border-border bg-card rounded-sm p-6 min-h-[70vh]" data-testid="item-form-page" onKeyDownCapture={handleSimpleFormNav}>
        <div className="flex items-center justify-between border-b border-border pb-3 mb-4">
          <div className="flex items-center gap-3">
            <Button type="button" data-testid="item-form-back-btn" variant="outline" onClick={() => { setOpen(false); setF(empty); setEditId(null); }} className="rounded-sm text-xs uppercase tracking-wider"><ArrowLeft className="w-3 h-3 mr-1"/>Back to List</Button>
            <h2 className="font-display uppercase text-lg">{editId ? "Edit" : "New"} Item</h2>
          </div>
          <Button type="button" data-testid="item-form-cancel-btn" variant="ghost" onClick={() => { setOpen(false); setF(empty); setEditId(null); }} className="rounded-sm text-xs uppercase tracking-wider text-muted-foreground">Cancel</Button>
        </div>
        {lockNonPrice && (
          <div className="text-[11px] text-muted-foreground bg-secondary/60 border border-border rounded-sm px-3 py-2 mb-3" data-testid="price-only-note">
            Only MRP and Per Unit Cost can be edited — all other item details are managed by platform master data.
          </div>
        )}
              <div className="grid grid-cols-3 gap-3">
                {baseFields.map(([k, label]) => (
                  <div key={k} className={k === "item_name" ? "col-span-2" : ""}>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{label}</Label>
                    <Input
                      data-testid={`item-${k}`}
                      value={f[k] ?? ""}
                      onChange={(e) => setF(x => ({ ...x, [k]: e.target.value }))}
                      disabled={lockNonPrice}
                      className="mt-1 rounded-sm bg-secondary border-border"
                    />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <MasterSelect label="Company" testid="item-company" value={f.company} options={companies} optKey="name" disabled={lockNonPrice}
                  onChange={(v) => setF(x => ({ ...x, company: v }))}
                  onAdd={() => setAddDlg("company")} />
                <MasterSelect label="Category" testid="item-category" value={f.category} options={catOpts} optKey="name" disabled={lockNonPrice}
                  onChange={(v) => setF(x => ({ ...x, category: v }))}
                  onAdd={() => setAddDlg("category")} />
                <MasterSelect label="Group" testid="item-group" value={f.group} options={grpOpts} optKey="name" disabled={lockNonPrice}
                  onChange={(v) => setF(x => ({ ...x, group: v }))}
                  onAdd={() => setAddDlg("group")} />
                <MasterSelect label="HSN Code" testid="item-hsn" value={f.hsn_code} options={hsnOpts} optKey="hsn_code" disabled={lockNonPrice}
                  onChange={(v) => setF(x => ({ ...x, hsn_code: v }))}
                  onAdd={() => setAddDlg("hsn")} />
                <MasterSelect label="Sector" testid="item-sector" value={f.sector} options={sectors.filter(s => s.status !== "Inactive")} optKey="name" disabled={lockNonPrice}
                  onChange={(v) => setF(x => ({ ...x, sector: v }))}
                  onAdd={() => setAddDlg("sector")} />
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Warranty</Label>
                  <Select value={f.warranty} onValueChange={(v) => setF(x => ({ ...x, warranty: v }))} disabled={lockNonPrice}>
                    <SelectTrigger data-testid="item-warranty" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="No">No</SelectItem>
                      <SelectItem value="Yes">Yes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {f.warranty === "Yes" && (
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Warranty Months</Label>
                    <Input data-testid="item-warranty-months" type="number" min="1" value={f.warranty_months ?? ""} onChange={(e) => setF(x => ({ ...x, warranty_months: e.target.value }))} disabled={lockNonPrice} className="mt-1 rounded-sm bg-secondary border-border" placeholder="e.g. 12"/>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-3 gap-3 mt-3">
                {numFields.map(([k, label]) => (
                  <div key={k}>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{label}</Label>
                    <Input
                      data-testid={`item-${k}`}
                      type="number"
                      value={f[k] ?? ""}
                      onChange={(e) => setF(x => ({ ...x, [k]: e.target.value }))}
                      disabled={lockNonPrice && k !== "mrp" && k !== "per_unit_cost"}
                      className="mt-1 rounded-sm bg-secondary border-border"
                    />
                  </div>
                ))}
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Total GST % (auto)</Label>
                  <Input
                    data-testid="item-total-gst"
                    type="number"
                    value={(Number(f.cgst) || 0) + (Number(f.sgst) || 0)}
                    readOnly
                    disabled
                    className="mt-1 rounded-sm bg-secondary border-border text-muted-foreground"
                  />
                </div>
              </div>
              <div className="mt-3">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">
                  Item Code {canOverrideCode ? "(auto-generated · blank = auto · override allowed)" : "(auto-generated, read-only)"}
                </Label>
                {canOverrideCode ? (
                  <Input
                    data-testid="item-code"
                    value={f.item_code ?? ""}
                    onChange={(e) => setF(x => ({ ...x, item_code: e.target.value.toUpperCase() }))}
                    disabled={lockNonPrice}
                    placeholder={codePreview ? `${codePreview}-0001 (auto)` : "Select Sector, Group, Category & Company to auto-generate"}
                    className="mt-1 rounded-sm bg-secondary border-border font-mono uppercase"
                  />
                ) : (
                  <Input
                    data-testid="item-code"
                    value={f.item_code || (codePreview ? `${codePreview}-0001 (auto)` : "")}
                    readOnly
                    disabled
                    className="mt-1 rounded-sm bg-secondary border-border font-mono text-muted-foreground"
                  />
                )}
                {!f.item_code && codePreview && (
                  <div className="text-[10px] text-muted-foreground mt-1 font-mono">Sequence number is assigned on save: {codePreview}-XXXX</div>
                )}
              </div>
              <Button data-testid="save-item-btn" onClick={save} disabled={!f.part_number || !f.item_name || (!editId && !f.mrp) || !f.company || !f.category || !f.group || !f.hsn_code}
                className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] mt-3">
                {editId ? "Update Item" : "Save Item"}
              </Button>
      </div>
      )}

      {!open && (<>

      <Dialog open={!!addDlg} onOpenChange={(v) => { if (!v) { setAddDlg(null); setAf({ name: "", description: "", cgst: 9, sgst: 9 }); } }}>
        <DialogContent className="rounded-sm bg-card border-border max-w-sm">
          <DialogHeader><DialogTitle className="font-display uppercase">New {addDlg && ADD_CFG[addDlg].label}</DialogTitle></DialogHeader>
          <div className="space-y-3" onKeyDownCapture={handleSimpleFormNav}>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{addDlg === "hsn" ? "HSN Code" : "Name"}</Label>
              <Input data-testid="addnew-name" value={af.name} onChange={(e) => setAf(x => ({ ...x, name: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" />
            </div>
            {addDlg === "hsn" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">CGST %</Label>
                  <Input data-testid="addnew-cgst" type="number" value={af.cgst} onChange={(e) => setAf(x => ({ ...x, cgst: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">SGST %</Label>
                  <Input data-testid="addnew-sgst" type="number" value={af.sgst} onChange={(e) => setAf(x => ({ ...x, sgst: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" />
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Description (optional)</Label>
              <Input data-testid="addnew-desc" value={af.description} onChange={(e) => setAf(x => ({ ...x, description: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" />
            </div>
            <Button data-testid="addnew-save" onClick={saveNew} disabled={!af.name} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="flex flex-wrap items-end gap-3 mb-4 border border-border bg-card rounded-sm p-3" data-testid="item-filter-bar" onKeyDownCapture={handleSimpleFormNav}>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Company</Label>
          <Select value={fCompany} onValueChange={setFCompany}>
            <SelectTrigger data-testid="filter-company" className="mt-1 rounded-sm bg-secondary border-border w-44 h-9"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Companies</SelectItem>
              {companies.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Category</Label>
          <Select value={fCategory} onValueChange={setFCategory}>
            <SelectTrigger data-testid="filter-category" className="mt-1 rounded-sm bg-secondary border-border w-44 h-9"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Group</Label>
          <Select value={fGroup} onValueChange={setFGroup}>
            <SelectTrigger data-testid="filter-group" className="mt-1 rounded-sm bg-secondary border-border w-44 h-9"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Groups</SelectItem>
              {groups.map(g => <SelectItem key={g.id} value={g.name}>{g.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">HSN Code</Label>
          <Select value={fHsn} onValueChange={setFHsn}>
            <SelectTrigger data-testid="filter-hsn" className="mt-1 rounded-sm bg-secondary border-border w-44 h-9"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All HSN Codes</SelectItem>
              {hsnCodes.map(h => <SelectItem key={h.id} value={h.hsn_code}>{h.hsn_code}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {filtered.length === 0 && (
        <div className="border border-border bg-card p-8 rounded-sm text-center text-muted-foreground">
          {q ? "No items match your search." : "No items yet — add one to make it selectable on job cards."}
        </div>
      )}

      <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">Part #</th>
              <th className="text-left p-3">Item</th>
              <th className="text-left p-3">Company</th>
              <th className="text-left p-3">Category</th>
              <th className="text-left p-3">Group</th>
              <th className="text-left p-3">HSN</th>
              <th className="text-right p-3">Total GST</th>
              <th className="text-left p-3">Unit</th>
              <th className="text-right p-3">MRP</th>
              <th className="text-right p-3">MOQ</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pg.pageItems.map(it => (
              <tr key={it.id} data-testid={`item-row-${it.id}`} className="border-t border-border hover:bg-secondary/50">
                <td className="p-3 font-mono text-xs text-primary uppercase">{it.part_number}</td>
                <td className="p-3">{it.item_name}</td>
                <td className="p-3 text-xs">{it.company || "-"}</td>
                <td className="p-3 text-xs text-muted-foreground">{it.category || "-"}</td>
                <td className="p-3 text-xs text-muted-foreground">{it.group || "-"}</td>
                <td className="p-3 font-mono text-xs">{it.hsn_code || "-"}</td>
                <td className="p-3 text-right font-mono text-xs text-primary" data-testid={`item-total-gst-${it.id}`}>{(Number(it.cgst) || 0) + (Number(it.sgst) || 0)}%</td>
                <td className="p-3 text-xs text-muted-foreground" data-testid={`item-unit-${it.id}`}>{it.unit}</td>
                <td className="p-3 text-right font-mono">{inr(it.mrp)}</td>
                <td className="p-3 text-right font-mono text-xs">{it.min_order_qty}</td>
                <td className="p-3 text-right">
                  <Button data-testid={`edit-item-${it.id}`} size="sm" variant="ghost" onClick={() => edit(it)} className="h-7 w-7 p-0"><Pencil className="w-3 h-3"/></Button>
                  <Button data-testid={`del-item-${it.id}`} size="sm" variant="ghost" onClick={() => del(it.id)} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3 h-3"/></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>
      </>)}
    </div>
  );
}
